// NF-e 55 XML receipt preview. The browser NEVER attests an NF-e signature or SEFAZ status.
// No stock may be written without the separate authorized, idempotent server RPC.
const onlyDigits = value => String(value || '').replace(/\D/g, '');
const direct = (node, name) => Array.from(node?.children || []).find(child => child.localName === name) || null;
const childText = (node, name) => (direct(node, name)?.textContent || '').trim();
export function validCnpj(value) {
  const digits = String(value || '').replace(/\D/g,'');
  if (!/^\d{14}$/.test(digits) || /^(\d)\1{13}$/.test(digits)) return false;
  const digit = (base, weights) => {
    const total = [...base].reduce((sum, n, i) => sum + Number(n) * weights[i], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };
  const first = digit(digits.slice(0,12),[5,4,3,2,9,8,7,6,5,4,3,2]);
  const second = digit(digits.slice(0,13),[6,5,4,3,2,9,8,7,6,5,4,3,2]);
  return first === Number(digits[12]) && second === Number(digits[13]);
}
const decimal = (text, label) => {
  const raw = String(text || '').trim();
  if (!/^(0|[1-9]\d*)(\.\d{1,10})?$/.test(raw)) throw Error('Número inválido em ' + label);
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > 1000000000) throw Error('Quantidade/valor fora do limite: ' + label);
  return n;
};
export function validNfeKey(key) {
  if (!/^\d{44}$/.test(key)) return false;
  let total = 0, weight = 2;
  for (let i = 42; i >= 0; i--) { total += Number(key[i]) * weight; weight = weight === 9 ? 2 : weight + 1; }
  const digit = 11 - total % 11;
  return Number(key[43]) === (digit >= 10 ? 0 : digit);
}
export function parseNfeXml(xml, Parser = globalThis.DOMParser) {
  if (typeof xml !== 'string' || xml.length > 3_000_000 || xml.length < 100 ||
      /<!DOCTYPE|<!ENTITY|<\?xml-stylesheet/i.test(xml)) throw Error('XML fora do formato ou limite permitido.');
  if (typeof Parser !== 'function') throw Error('Este navegador não possui leitor de XML.');
  const parsed = new Parser().parseFromString(xml, 'application/xml');
  if (parsed.getElementsByTagName('parsererror').length) throw Error('XML malformado.');
  const root = parsed.documentElement;
  if (root?.localName !== 'nfeProc' || root.namespaceURI !== 'http://www.portalfiscal.inf.br/nfe')
    throw Error('Envie o XML completo autorizado (nfeProc), não apenas a imagem/DANFE.');
  const note = direct(root, 'NFe'), info = direct(note, 'infNFe');
  const proto = direct(direct(root, 'protNFe'), 'infProt');
  if (!info || !proto || !direct(note, 'Signature')) throw Error('XML sem NF-e assinada e protocolo.');
  const ide = direct(info, 'ide'), emitter = direct(info, 'emit'), recipient = direct(info, 'dest');
  if (childText(ide, 'mod') !== '55') throw Error('A entrada exige NF-e modelo 55. NFC-e 65 não serve para recebimento.');
  if (childText(ide, 'tpAmb') !== '1' || childText(proto, 'tpAmb') !== '1')
    throw Error('NF-e de homologação não altera estoque real.');
  const key = String(info.getAttribute('Id') || '').replace(/^NFe/, '');
  if (!validNfeKey(key) || childText(proto, 'chNFe') !== key)
    throw Error('Chave de acesso inválida ou diferente do protocolo.');
  if (!['100', '150'].includes(childText(proto, 'cStat')) || !childText(proto, 'nProt'))
    throw Error('O XML não declara autorização de uso.');
  const supplierCnpj = onlyDigits(childText(emitter, 'CNPJ'));
  const recipientCnpj = onlyDigits(childText(recipient, 'CNPJ'));
  if (!validCnpj(supplierCnpj) || !validCnpj(recipientCnpj) || supplierCnpj === recipientCnpj)
    throw Error('Emitente ou destinatário CNPJ inválido.');
  const itemNodes = Array.from(info.children).filter(el => el.localName === 'det');
  if (!itemNodes.length || itemNodes.length > 500) throw Error('A nota precisa de 1 a 500 itens.');
  const seenLines = new Set();
  const lines = itemNodes.map(node => {
    const product = direct(node, 'prod'), lineNumber = Number(node.getAttribute('nItem'));
    if (!Number.isSafeInteger(lineNumber) || lineNumber < 1 || seenLines.has(lineNumber) || !product)
      throw Error('Item NF-e duplicado ou inválido.');
    seenLines.add(lineNumber);
    const quantity = decimal(childText(product, 'qCom'), 'quantidade');
    const price = decimal(childText(product, 'vUnCom'), 'preço');
    const value = decimal(childText(product, 'vProd'), 'total');
    if (Math.abs(Math.round(quantity * price * 100) - Math.round(value * 100)) > 2)
      throw Error('Divergência de valor no item ' + lineNumber);
    const barcode = [childText(product, 'cEAN'), childText(product, 'cEANTrib')]
      .map(onlyDigits).find(code => /^\d{8,14}$/.test(code)) || '';
    return {
      line: lineNumber, supplierCode: childText(product, 'cProd').slice(0, 60),
      name: childText(product, 'xProd').slice(0, 200), barcode,
      unit: childText(product, 'uCom').trim().toUpperCase().slice(0, 12),
      quantity, unitPrice: price, total: value
    };
  });
  return {
    key, supplierCnpj, recipientCnpj, protocol: childText(proto, 'nProt'),
    issueDate: childText(ide, 'dhEmi') || childText(ide, 'dEmi'),
    supplierName: childText(emitter, 'xNome').slice(0, 160), lines,
    fiscalProof: false, // XML structure is NOT cryptographic proof or a live SEFAZ lookup.
    authorizationClaim: childText(proto, 'cStat')
  };
}
export function matchNfeItems(lines, products = []) {
  const byBarcode = new Map();
  for (const product of products) {
    if (!product.barcode) continue;
    const key = onlyDigits(product.barcode);
    if (!byBarcode.has(key)) byBarcode.set(key, []);
    byBarcode.get(key).push(product);
  }
  return lines.map(line => {
    const candidates = line.barcode ? byBarcode.get(line.barcode) || [] : [];
    const candidate = candidates.length === 1 ? candidates[0] : null;
    const status = candidates.length > 1 ? 'ambiguous' : !candidate ? 'unmatched'
      : candidate.active === false || !candidate.track_inventory ? 'inactive'
      : String(candidate.unit || '').toUpperCase() !== line.unit ? 'unit_mismatch'
      : !candidate.allow_fractional && line.quantity !== Math.trunc(line.quantity) ? 'fractional'
      : 'ready';
    return { ...line, productId: status === 'ready' ? candidate.id : '', status };
  });
}
export function prepareNfeReceipt(note, rows, xmlHash) {
  if (!note || !validNfeKey(note.key) || ! /^[a-f0-9]{64}$/.test(xmlHash || ''))
    throw Error('NF-e ou SHA-256 inválido.');
  if (!Array.isArray(rows) || !rows.length || rows.length !== note.lines.length ||
      rows.some(row => row.status !== 'ready' || !/^[0-9a-f-]{36}$/i.test(row.productId || '')))
    throw Error('Concilie todos os produtos e unidades antes da entrada.');
  // Multiple NF-e lines may point at the same catalog item; combine before atomic posting.
  const sums = new Map();
  for (const row of rows) {
    sums.set(row.productId, Number(((sums.get(row.productId) || 0) + row.quantity).toFixed(3)));
  }
  return {
    access_key: note.key, supplier_cnpj: note.supplierCnpj,
    recipient_cnpj: note.recipientCnpj, xml_sha256: xmlHash,
    lines: Array.from(sums, ([product_id, quantity]) => ({product_id, quantity})),
    source_line_count: rows.length
  };
}
